/** A picker mounted on demand (plan §5): at rest the picked name in a box of the input's
 *  geometry; focused or clicked, the operator's tour picker (`RemoteAutocomplete`, open at
 *  once). Its searches go through the TanStack cache under stable keys
 *  (`['tours', 'picker', <tenant>, <kind>, <query>]`), so every activity picker of the tour
 *  shares one list, and nothing is requested at rest or per keystroke elsewhere. A failed
 *  search says so in the list, with Retry. */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import * as toursApi from '@/api/tours';
import type { PickerKind, PickerOption } from '@/api/tours';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';

const FIVE_MINUTES = 5 * 60 * 1000;

export const pickerKey = (tenantId: number, kind: PickerKind, q: string, includeParent = false) =>
  ['tours', 'picker', tenantId, kind, q, ...(includeParent ? ['parent'] : [])] as const;

/** The cached search of one picker kind (`includeParent`: activities a SAVED row may
 *  reference — the operator's and its franchise parent's). */
export function usePickerSearch(tenantId: number, kind: PickerKind, includeParent = false) {
  const qc = useQueryClient();
  return useCallback(
    async (q: string) =>
      (
        await qc.fetchQuery({
          queryKey: pickerKey(tenantId, kind, q, includeParent),
          queryFn: () => toursApi.picker(tenantId, kind, q || undefined, undefined, undefined, undefined, includeParent),
          staleTime: FIVE_MINUTES,
        })
      ).results,
    [qc, tenantId, kind, includeParent],
  );
}

const optionKey = (o: PickerOption) => o.id;

/** A currency's code: the read carries only its id, so the operator's (short) currency
 *  list is fetched once — through the pickers' cache, when a currency is on screen — and
 *  every currency field reads it. Other kinds name their value from what the tour knows. */
export function usePickerLabel(kind: PickerKind, tenantId: number, id: unknown, fallback: string): string {
  const currencies = useQuery({
    queryKey: pickerKey(tenantId, 'currencies', ''),
    queryFn: () => toursApi.picker(tenantId, 'currencies'),
    staleTime: FIVE_MINUTES,
    enabled: kind === 'currencies' && typeof id === 'number',
  });
  if (kind !== 'currencies' || typeof id !== 'number') return fallback;
  const hit = currencies.data?.results.find((o) => o.id === id);
  return hit ? (hit.code ?? hit.name ?? fallback) : fallback;
}

export interface LazyPickerProps {
  id: string;
  label: string;
  kind: PickerKind;
  tenantId: number;
  value: number | null;
  /** The value's name (`TourNames`). */
  valueLabel: string;
  optionLabel?: (o: PickerOption) => string;
  errorText: string;
  error?: boolean;
  describedBy?: string;
  /** Activities of a SAVED row: the franchise parent's are listed too. */
  includeParent?: boolean;
  onChange: (option: PickerOption | null) => void;
}

const defaultLabel = (o: PickerOption) => o.name ?? `#${o.id}`;

/** A picked value as text (view mode). */
export function PickerText({ kind, tenantId, id, fallback }: { kind: PickerKind; tenantId: number; id: unknown; fallback: string }) {
  const text = usePickerLabel(kind, tenantId, id, fallback);
  const empty = id === null || id === undefined;
  return (
    <Typography variant="body2" component="span" sx={{ color: empty ? 'text.disabled' : 'text.primary', overflowWrap: 'anywhere' }}>
      {empty ? '—' : text}
    </Typography>
  );
}

export function LazyPicker({ id, label, kind, tenantId, value, valueLabel: known, optionLabel = defaultLabel, errorText, error, describedBy, includeParent = false, onChange }: LazyPickerProps) {
  const [open, setOpen] = useState(false);
  const cached = usePickerSearch(tenantId, kind, includeParent);
  const valueLabel = usePickerLabel(kind, tenantId, value, known);
  // The current value is always among the options of the unfiltered list — even when the
  // operator's list is long, or it is a value the list does not carry.
  const search = useCallback(
    async (q: string) => {
      const results = await cached(q);
      if (q || value === null || results.some((o) => o.id === value)) return results;
      return [{ id: value, name: valueLabel }, ...results];
    },
    [cached, value, valueLabel],
  );
  const labelOf = useCallback(
    (o: PickerOption) => {
      const text = optionLabel(o);
      // A franchise parent's activity says whose it is.
      return o.tenant && o.tenant.id !== tenantId ? `${text} · ${o.tenant.code}` : text;
    },
    [optionLabel, tenantId],
  );
  if (open) {
    return (
      <Box sx={{ flex: '1 1 auto', minWidth: 0 }} data-picker="open">
        <RemoteAutocomplete<PickerOption>
          id={id}
          size="small"
          fetchOnEmpty
          autoFocus
          openOnFocus
          value={value === null ? null : { id: value, name: valueLabel }}
          getOptionLabel={labelOf}
          getOptionKey={optionKey}
          queryFn={search}
          errorText={errorText}
          error={error}
          inputProps={{ 'aria-label': label, ...(describedBy ? { 'aria-describedby': describedBy } : {}) }}
          onChange={(v) => {
            onChange((v as PickerOption | null) ?? null);
            setOpen(false);
          }}
          onBlur={() => setOpen(false)}
        />
      </Box>
    );
  }
  return (
    <Box
      id={id}
      role="combobox"
      aria-expanded={false}
      aria-label={label}
      aria-describedby={describedBy}
      tabIndex={0}
      data-picker="closed"
      onFocus={() => setOpen(true)}
      onClick={() => setOpen(true)}
      sx={{
        flex: '1 1 auto',
        minWidth: 0,
        minHeight: 40,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.5,
        border: 1,
        borderColor: error ? 'error.main' : 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        cursor: 'pointer',
        '&:hover': { borderColor: error ? 'error.main' : 'text.primary' },
        '&:focus-visible': { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: -1 },
      }}
    >
      <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0, color: value === null ? 'text.secondary' : 'text.primary' }}>
        {value === null ? 'None' : valueLabel}
      </Typography>
      <ArrowDropDownIcon aria-hidden sx={{ color: 'text.secondary' }} />
    </Box>
  );
}
